import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Landmark, Save, Loader2, AlertTriangle, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

const CAMPOS_IN = [
  'nome', 'tipo', 'banco', 'agencia', 'numero_conta', 'cor',
  'codigo_banco', 'agencia_dv', 'conta_dv', 'emite_boleto', 'carteira', 'variacao_carteira',
  'convenio', 'codigo_beneficiario', 'nosso_numero_inicial', 'nosso_numero_atual',
  'especie_titulo', 'aceite', 'layout_cnab', 'multa_pct', 'juros_mes_pct', 'desconto_pct',
  'dias_protesto', 'instrucoes', 'integracao_tipo', 'api_ambiente',
  'baixa_automatica', 'conciliacao_automatica', 'api_client_id', 'api_client_secret', 'api_token',
]

function Campo({ label, children, hint }: { label: string; children: any; hint?: string }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-ink-400 mt-0.5">{hint}</p>}
    </div>
  )
}

export default function ConfigBoletoPage() {
  const qc = useQueryClient()
  const { data: contas = [], isLoading } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  const [sel, setSel] = useState<number | null>(null)
  const [form, setForm] = useState<any>(null)

  useEffect(() => {
    if (sel == null && contas.length) {
      const def = contas.find((c: any) => c.emite_boleto) || contas[0]
      setSel(def.id)
    }
  }, [contas])
  useEffect(() => {
    const c = contas.find((x: any) => x.id === sel)
    if (c) setForm({ ...c })
  }, [sel, contas])

  const salvar = useMutation({
    mutationFn: async () => {
      const payload: any = {}
      for (const k of CAMPOS_IN) payload[k] = form[k] ?? null
      payload.nome = form.nome  // obrigatório
      // Próximo nº de remessa CNAB (mesclado no backend — não apaga o resto do api_config)
      const proxRem = form.api_config?.prox_num_remessa
      if (proxRem != null && proxRem !== '') payload.api_config = { prox_num_remessa: Number(proxRem) }
      return (await api.put(`/financeiro/contas-bancarias/${form.id}`, payload)).data
    },
    onSuccess: () => { toast.success('Configuração salva'); qc.invalidateQueries({ queryKey: ['contas-bancarias'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }))
  const inp = (k: string, props: any = {}) => (
    <input className="input" value={form?.[k] ?? ''} onChange={e => set(k, e.target.value)} {...props} />
  )

  if (isLoading) return <div className="p-8 text-center text-ink-400"><Loader2 className="animate-spin inline" /></div>

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2"><Landmark size={18} /> Configuração de Boleto</h2>

      <div className="card card-body bg-amber-50 border-amber-200 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Parâmetros de cobrança por banco (carteira, convênio, nosso número, encargos, instruções e layout CNAB).
          Confira contra o contrato de cobrança do banco antes de emitir em produção (a carteira do Santander veio como padrão 101).</span>
      </div>

      {/* Seletor de banco */}
      <div className="flex flex-wrap gap-2">
        {contas.map((c: any) => (
          <button key={c.id} onClick={() => setSel(c.id)}
            className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${sel === c.id ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium' : 'border-ink-200 bg-white'}`}>
            {c.nome} {c.emite_boleto && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">boleto</span>}
          </button>
        ))}
      </div>

      {form && (
        <div className="space-y-4">
          {/* Conta */}
          <div className="card">
            <div className="px-5 py-3 border-b font-semibold text-sm">Conta bancária</div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Campo label="Nome">{inp('nome')}</Campo>
              <Campo label="Banco (FEBRABAN)" hint="033 Santander · 004 BNB">{inp('codigo_banco', { maxLength: 5 })}</Campo>
              <Campo label="Agência">{inp('agencia')}</Campo>
              <Campo label="Dígito agência">{inp('agencia_dv', { maxLength: 2 })}</Campo>
              <Campo label="Conta">{inp('numero_conta')}</Campo>
              <Campo label="Dígito conta">{inp('conta_dv', { maxLength: 2 })}</Campo>
              <Campo label="Emite boleto?">
                <select className="input" value={form.emite_boleto ? '1' : '0'} onChange={e => set('emite_boleto', e.target.value === '1')}>
                  <option value="1">Sim</option><option value="0">Não</option>
                </select>
              </Campo>
            </div>
          </div>

          {/* Cobrança */}
          <div className="card">
            <div className="px-5 py-3 border-b font-semibold text-sm">Cobrança</div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Campo label="Carteira">{inp('carteira', { maxLength: 10 })}</Campo>
              <Campo label="Variação carteira">{inp('variacao_carteira', { maxLength: 5 })}</Campo>
              <Campo label="Convênio">{inp('convenio')}</Campo>
              <Campo label="Cód. beneficiário/cedente">{inp('codigo_beneficiario')}</Campo>
              <Campo label="Nosso número inicial">{inp('nosso_numero_inicial')}</Campo>
              <Campo label="Nosso número atual" hint="próximo a usar — cuidado ao mexer">{inp('nosso_numero_atual')}</Campo>
              <Campo label="Próximo nº de remessa" hint="sequência do arquivo CNAB (vem do WinThor) — cuidado ao mexer">
                <input className="input" type="number"
                  value={form.api_config?.prox_num_remessa ?? ''}
                  onChange={e => setForm((f: any) => ({ ...f, api_config: { ...(f.api_config || {}), prox_num_remessa: e.target.value === '' ? null : Number(e.target.value) } }))} />
              </Campo>
              <Campo label="Espécie título" hint="DM = duplicata">{inp('especie_titulo', { maxLength: 10 })}</Campo>
              <Campo label="Aceite">
                <select className="input" value={form.aceite || 'N'} onChange={e => set('aceite', e.target.value)}>
                  <option value="N">N</option><option value="S">S</option>
                </select>
              </Campo>
              <Campo label="Layout CNAB">
                <select className="input" value={form.layout_cnab || ''} onChange={e => set('layout_cnab', e.target.value)}>
                  <option value="">—</option><option value="240">240</option><option value="400">400</option>
                </select>
              </Campo>
            </div>
          </div>

          {/* Encargos + layout/conteúdo */}
          <div className="card">
            <div className="px-5 py-3 border-b font-semibold text-sm flex items-center gap-2"><FileText size={14} /> Encargos e instruções (saem no boleto)</div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Campo label="Multa (%)">{inp('multa_pct', { type: 'number', step: '0.01' })}</Campo>
              <Campo label="Juros ao mês (%)">{inp('juros_mes_pct', { type: 'number', step: '0.01' })}</Campo>
              <Campo label="Desconto (%)">{inp('desconto_pct', { type: 'number', step: '0.01' })}</Campo>
              <Campo label="Dias p/ protesto">{inp('dias_protesto', { type: 'number' })}</Campo>
              <div className="col-span-2 md:col-span-4">
                <Campo label="Instruções (texto que aparece no boleto)">
                  <textarea className="input" rows={3} value={form.instrucoes || ''} onChange={e => set('instrucoes', e.target.value)} />
                </Campo>
              </div>
            </div>
          </div>

          {/* Integração: registro + baixa automática + conciliação (motor = F3) */}
          <div className="card">
            <div className="px-5 py-3 border-b font-semibold text-sm">Integração — registro, baixa automática e conciliação</div>
            <div className="px-5 pt-3 text-[11px] text-ink-500">Define COMO o banco se integra. A automação (processar retorno/API pra dar baixa e conciliar) é ativada aqui; o motor é entregue na F3.</div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Campo label="Tipo de integração">
                <select className="input" value={form.integracao_tipo || ''} onChange={e => set('integracao_tipo', e.target.value)}>
                  <option value="">— manual —</option><option value="cnab">CNAB (remessa/retorno)</option><option value="api">API do banco</option>
                </select>
              </Campo>
              <Campo label="Ambiente">
                <select className="input" value={form.api_ambiente || ''} onChange={e => set('api_ambiente', e.target.value)}>
                  <option value="">—</option><option value="homologacao">Homologação</option><option value="producao">Produção</option>
                </select>
              </Campo>
              <Campo label="Baixa automática do boleto" hint="dá baixa na conta a receber quando o banco confirma o pagamento">
                <select className="input" value={form.baixa_automatica ? '1' : '0'} onChange={e => set('baixa_automatica', e.target.value === '1')}>
                  <option value="0">Desligada</option><option value="1">Ligada</option>
                </select>
              </Campo>
              <Campo label="Conciliação bancária automática" hint="concilia o extrato com os lançamentos">
                <select className="input" value={form.conciliacao_automatica ? '1' : '0'} onChange={e => set('conciliacao_automatica', e.target.value === '1')}>
                  <option value="0">Desligada</option><option value="1">Ligada</option>
                </select>
              </Campo>
              <Campo label="API client_id">{inp('api_client_id')}</Campo>
              <Campo label="API client_secret" hint="deixe em branco p/ manter o atual">
                <input className="input" type="password" placeholder="••••••" value={form.api_client_secret ?? ''} onChange={e => set('api_client_secret', e.target.value)} />
              </Campo>
              <Campo label="API token" hint="deixe em branco p/ manter o atual">
                <input className="input" type="password" placeholder="••••••" value={form.api_token ?? ''} onChange={e => set('api_token', e.target.value)} />
              </Campo>
            </div>
          </div>

          <div className="flex justify-end">
            <button className="btn-primary" disabled={salvar.isPending} onClick={() => salvar.mutate()}>
              {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar configuração
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
